import "./Overview.css";

import { EditGreyIcon } from "../../../../../assets";
import { OrganisationTypeConstants } from "../../../../../constants";
import { EditDatesPanel } from "../../../../../templates";
import { hasStandardSpecificDefinition } from "../../../../../utils";
import {
  ActionButtons,
  Column,
  ErrorBox,
  HorizontalLifecycle,
  PhaseLifecycle,
  Row,
  Select,
} from "../../../../../widget";
import { GenericModal } from "../../../../developer/components";
import {
  BadgesSection,
  BasicDetailsSection,
  CertificationsSection,
  DatesSection,
  EditableBasic,
  StandardSpecificSection,
} from "./components";
import { GroupSection } from "./components/GroupSection";
import { useOverview } from "./useOverview";

const Overview = (): JSX.Element => {
  const {
    currentUserType,
    projectDetails,
    projectDates,
    setProjectDates,
    currentStatus,
    errors,
    isEditable,
    isReadOnly,
    setIsReadOnly,
    basicDetailsData,
    setBasicDetailsData,
    locationData,
    setLocationData,
    onCancel,
    onSubmit,
    validatorData,
    certificateData,
    badgeData,
    standardSpecificDefinition,
    isNoStandard,
    standardOptions,
    selectedStandardUuid,
    showStandardChangeModal,
    onStandardSelect,
    onCancelStandardChange,
    isOnSubmitLoading,
    showUpdateGroupVVBModal,
    setShowUpdateGroupVVBModal,
    showEditDatesPanel,
    setShowEditDatesPanel,
    shouldRefreshProjectDetails,
  } = useOverview();

  return (
    <>
      <div className="ProjectTabSection">
        <Row spacingV={currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ? "ll" : "2xl"}>
          <Column span={6}>
            <h2>Overview</h2>
          </Column>
          <Column span={6} justify="between">
            {isEditable && isReadOnly && (
              <div className="editOverviewButton">
                <EditGreyIcon onClick={() => setIsReadOnly(false)} />
              </div>
            )}
          </Column>
        </Row>
        {errors && errors.length > 0 && (
          <Row spacingV="ml">
            <Column span={4}>
              <ErrorBox
                title="Please solve the below issues before saving"
                text={errors.map((error) => error.message)}
              />
            </Column>
          </Row>
        )}
        <Row spacingV={isEditable ? "2xl" : "ll"}>
          {[OrganisationTypeConstants.DEVELOPMENT_MANAGER, OrganisationTypeConstants.DEVELOPER].includes(
            currentUserType
          ) ? (
            <Column span={12}>
              <PhaseLifecycle dates={projectDates?.dates} handleEditDatesClick={() => setShowEditDatesPanel(true)} />
            </Column>
          ) : (
            <Column span={7}>
              <Row spacingV="ml">
                <Column span={12}>
                  <h3>Lifecycle</h3>
                </Column>
              </Row>
              <HorizontalLifecycle currentStatus={currentStatus} />
            </Column>
          )}
        </Row>
        <Row>
          <Column span={12}>
            <form onSubmit={onSubmit}>
              {isNoStandard && isEditable && !isReadOnly && (
                <Row spacingV="ll">
                  <Column span={4}>
                    <Select
                      label="Standard"
                      placeholder="Select"
                      data={standardOptions}
                      value={selectedStandardUuid}
                      onChange={onStandardSelect}
                      corners="rounded"
                    />
                  </Column>
                </Row>
              )}
              {projectDetails && !isReadOnly ? (
                <EditableBasic
                  projectDetails={projectDetails}
                  basicDetailsData={basicDetailsData}
                  setBasicDetailsData={setBasicDetailsData}
                  locationData={locationData}
                  setLocationData={setLocationData}
                  validatorData={validatorData}
                  isNoStandard={isNoStandard}
                />
              ) : (
                projectDetails && (
                  <BasicDetailsSection
                    projectDetails={projectDetails}
                    basicDetailsData={basicDetailsData}
                    setBasicDetailsData={setBasicDetailsData}
                    locationData={locationData}
                    isReadOnly={isReadOnly}
                    validatorData={validatorData}
                    isNoStandard={isNoStandard}
                  />
                )
              )}
              {certificateData.length > 0 && <CertificationsSection certificates={certificateData} />}
              {badgeData.length > 0 && <BadgesSection badges={badgeData} />}
              {!isReadOnly && (
                <Row className="OverviewActionButtons">
                  <Column span={2}>
                    <ActionButtons onCancel={onCancel} isLoading={isOnSubmitLoading} />
                  </Column>
                </Row>
              )}
            </form>
          </Column>
        </Row>
      </div>
      {standardSpecificDefinition && hasStandardSpecificDefinition(standardSpecificDefinition) && (
        <div className="ProjectTabSection">
          <StandardSpecificSection
            standardSpecificDefinition={standardSpecificDefinition}
            hasWriteProjectDetailsPermission={isEditable}
          />
        </div>
      )}
      <DatesSection handleEditDatesClick={() => setShowEditDatesPanel(true)} />

      {projectDetails?.group?.groupUuid && projectDetails.uuid && (
        <div className="ProjectTabSection">
          <GroupSection groupUuid={projectDetails?.group?.groupUuid} currentProjectUuid={projectDetails.uuid} />
        </div>
      )}
      <GenericModal
        show={showUpdateGroupVVBModal}
        onConfirm={() => onSubmit(undefined)}
        closeModal={() => setShowUpdateGroupVVBModal(false)}
        data={{
          title: "Update group vvb",
          text: [
            "All projects in a group must have the same VVB. Since this project is part of a group, by updating the VVB for this project all other projects in the group will be updated with the same VVB. ",
          ],
          confirmText: "Update",
          cancelText: "Cancel",
        }}
        isLoading={isOnSubmitLoading}
      />
      <GenericModal
        show={showStandardChangeModal}
        onConfirm={() => onSubmit(undefined)}
        closeModal={onCancelStandardChange}
        data={{
          title: "Assign standard",
          text: [
            "Are you sure you want to assign this standard to the project? This cannot be undone and will configure the project against the selected governance standard.",
          ],
          confirmText: "Confirm",
          cancelText: "Cancel",
        }}
        isLoading={isOnSubmitLoading}
      />
      {[OrganisationTypeConstants.DEVELOPMENT_MANAGER, OrganisationTypeConstants.DEVELOPER].includes(currentUserType) &&
        projectDates && (
          <EditDatesPanel
            data={projectDates}
            setData={setProjectDates}
            show={showEditDatesPanel}
            onClose={() => setShowEditDatesPanel(false)}
            canShowCompletePhaseToggle
            shouldRefreshProjectDetails={shouldRefreshProjectDetails}
          />
        )}
    </>
  );
};

export default Overview;
